/**
 * turnSettings — the per-turn wire settings derived from the composer's
 * selections: which knowledge source, which tools' flags (a turn can select
 * several tools), which image-gen model.
 *
 * **Role in the chat module.** There are TWO ways to start a turn — typing in the
 * composer, and re-running an existing one (edit / regenerate / interrupt resume) — and
 * both must send the same settings for the same selections.
 */

import type { SendMessagePayload } from 'src/content/ChatV2/commands/SendMessageCommand';
import {
  getAvailableTools,
  getToolsPayloadFlags
} from 'src/content/ChatV2/components/Composer/toolbar/toolRegistry';
import {
  CHAT_TOOL,
  type IMAGE_GEN_TOOL,
  KNOWLEDGE_TYPE
} from 'src/content/ChatV2/enums';
import type { IsGranted } from 'src/content/ChatV2/lib/permissions/chatFeatureAccess';

/** The composer selections a turn is derived from. */
export interface TurnSettingsInput {
  chatType: KNOWLEDGE_TYPE;
  knowledge: KNOWLEDGE_TYPE;
  activeTools: readonly CHAT_TOOL[] | undefined;
  imageGenTool: IMAGE_GEN_TOOL;
  allowlist?: readonly CHAT_TOOL[];
  isGranted?: IsGranted;
  modelId?: number;
}

/**
 * Effective knowledge for a turn — rule 1 above, in one place because the
 * Composer needs it for tool availability and the wire needs it for
 * `knowledge_type`, and the two must agree.
 */
export function effectiveKnowledgeFor(
  chatType: KNOWLEDGE_TYPE,
  knowledge: KNOWLEDGE_TYPE
): KNOWLEDGE_TYPE {
  return chatType === KNOWLEDGE_TYPE.WORLD ? KNOWLEDGE_TYPE.WORLD : knowledge;
}

/**
 * The tools actually in effect for this turn — rule 2 above, applied per tool.
 * A selected tool that this context doesn't offer simply drops out; the others
 * still count.
 */
export function activeToolsFor(
  input: Pick<
    TurnSettingsInput,
    'chatType' | 'knowledge' | 'activeTools' | 'allowlist' | 'isGranted'
  >
): CHAT_TOOL[] {
  const selected = input.activeTools;
  if (!selected?.length) {
    return [];
  }
  const available = getAvailableTools({
    knowledge: effectiveKnowledgeFor(input.chatType, input.knowledge),
    allowlist: input.allowlist,
    isGranted: input.isGranted
  });
  return available
    .filter((tool) => selected.includes(tool.id))
    .map((tool) => tool.id);
}

/**
 * The settings slice of a send payload. Spread onto a fresh send (the composer) or onto
 * a re-run (edit / regenerate) — identical either way, which is the whole point of this
 * module.
 */
export type TurnSettings = Partial<SendMessagePayload> & {
  knowledgeType: KNOWLEDGE_TYPE;
  chatType: KNOWLEDGE_TYPE;
};

export function buildTurnSettings(input: TurnSettingsInput): TurnSettings {
  const activeTools = activeToolsFor(input);
  return {
    knowledgeType: effectiveKnowledgeFor(input.chatType, input.knowledge),
    chatType: input.chatType,
    ...(input.modelId !== undefined && { modelId: input.modelId }),
    ...getToolsPayloadFlags(activeTools),
    ...(activeTools.includes(CHAT_TOOL.IMAGE_GENERATION) && {
      imageGenerationTool: input.imageGenTool
    })
  };
}

/**
 * Row field the submitted settings are stamped onto.
 */
export const TURN_SETTINGS_FIELD = 'turnSettings';

/**
 * The settings a turn was SUBMITTED with, lifted off its payload.
 */
export function turnSettingsOfPayload(
  payload: SendMessagePayload
): TurnSettings {
  return {
    knowledgeType: payload.knowledgeType ?? payload.chatType,
    chatType: payload.chatType,
    ...(payload.modelId !== undefined && { modelId: payload.modelId }),
    ...(payload.webSearchEnabled && { webSearchEnabled: true }),
    ...(payload.imageGenerationEnabled && { imageGenerationEnabled: true }),
    ...(payload.imageGenerationTool !== undefined && {
      imageGenerationTool: payload.imageGenerationTool
    })
  };
}

/**
 * The stamp read back off a query row, or `undefined` for a row that predates this page
 * load.
 */
export function turnSettingsOfRow(row: {
  [key: string]: unknown;
}): TurnSettings | undefined {
  const stamped = row[TURN_SETTINGS_FIELD];
  return stamped && typeof stamped === 'object'
    ? (stamped as TurnSettings)
    : undefined;
}

/**
 * Settings for re-running a turn we have no stamp for — a row from before this page
 * load.
 */
export function fallbackTurnSettings(input: {
  chatType: KNOWLEDGE_TYPE;
  activeTools: readonly CHAT_TOOL[] | undefined;
  imageGenTool: IMAGE_GEN_TOOL;
  allowlist?: readonly CHAT_TOOL[];
}): TurnSettings {
  return buildTurnSettings({
    ...input,
    knowledge: input.chatType
  });
}
